/**
 * LayoutProvider
 * Provides layout context for content rendering
 */

import React, { createContext, useContext, useMemo } from 'react';
import type { LayoutConfig } from '../schema';

export interface LayoutContextValue {
    /** Current layout configuration */
    layout: LayoutConfig;
    /** Whether in mobile view */
    isMobile: boolean;
    /** Current breakpoint */
    breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /** Container width */
    containerWidth?: number;
}

const defaultLayout: LayoutConfig = {
    type: 'vertical',
    gap: '1rem',
    padding: '1rem',
};

const LayoutContext = createContext<LayoutContextValue>({
    layout: defaultLayout,
    isMobile: false,
    breakpoint: 'md',
});

export interface LayoutProviderProps {
    children: React.ReactNode;
    layout?: LayoutConfig;
}

export function LayoutProvider({
    children,
    layout = defaultLayout,
}: LayoutProviderProps): React.ReactElement {
    // Detect viewport size
    const [breakpoint, setBreakpoint] = React.useState<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md');
    const [containerWidth, setContainerWidth] = React.useState<number | undefined>();

    React.useEffect(() => {
        const updateBreakpoint = () => {
            const width = window.innerWidth;
            if (width < 640) setBreakpoint('xs');
            else if (width < 768) setBreakpoint('sm');
            else if (width < 1024) setBreakpoint('md');
            else if (width < 1280) setBreakpoint('lg');
            else setBreakpoint('xl');
            setContainerWidth(width);
        };

        updateBreakpoint();
        window.addEventListener('resize', updateBreakpoint);
        return () => window.removeEventListener('resize', updateBreakpoint);
    }, []);

    const isMobile = breakpoint === 'xs' || breakpoint === 'sm';

    const value = useMemo<LayoutContextValue>(
        () => ({
            layout,
            isMobile,
            breakpoint,
            containerWidth,
        }),
        [layout, isMobile, breakpoint, containerWidth]
    );

    return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayoutContext(): LayoutContextValue {
    return useContext(LayoutContext);
}

